import React, { useState, useRef, useEffect, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import Skeleton from '@mui/material/Skeleton';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";

const ImageOcclusion = () => {
  // ---- general states ----
  const [readOnlyExpandedCards, setReadOnlyExpandedCards] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [labels, setLabels] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [statement, setStatement] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentLabel, setCurrentLabel] = useState({ id: '', answer: '', explanation: '' });
  const [editingIndex, setEditingIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);
const [folderType, setFolderType] = useState(null); // 'own', 'free', or 'paid'
const [isAssignedFolder, setIsAssignedFolder] = useState(false);

  const [userGuideOpen, setUserGuideOpen] = useState(false);

  // saved questions / edit flows
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteQuestionId, setDeleteQuestionId] = useState(null);
  const [editQuestionDialogOpen, setEditQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [tags, setTags] = useState([]);
  const [editTagsInput, setEditTagsInput] = useState('');
  const [tagsInput, setTagsInput] = useState('');
 const previewCanvasRef = useRef(null); // refs & context
  const canvasRef = useRef(null);            // main canvas for creating questions
  const editCanvasRef = useRef(null);        // edit canvas inside Edit dialog
  const editBaseImageRef = useRef(null);     // cached Image object for edit canvas
  const { subfolder_id } = useParams();
  const navigate = useNavigate();
  const { mode, customColors } = useContext(ThemeContext);
// Add to state declarations
const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
const [previewQuestion, setPreviewQuestion] = useState(null);
const [revealedCount, setRevealedCount] = useState(0);

// Function to open preview dialog
const openPreviewDialog = (question) => {
  setRevealedCount(0);
  setPreviewQuestion(question);
  setPreviewDialogOpen(true);
};
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem('authToken');

  const colors = [
  '#ff0000',  // red
  '#0000ff',  // blue
  '#00ff00',  // green
  '#ffff00',
  '#ff00ff',
  '#00ffff',
  '#ff8000',
  '#8000ff',
];
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImageDataUrl, setEditImageDataUrl] = useState(null);


  // ---- dropzone for new questions ----
  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif'] },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setImageFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setImageDataUrl(e.target.result);
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => setOriginalImage(img);
        };
        reader.readAsDataURL(file);
      }
    }
  });

  useEffect(() => {
  const initializeComponent = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const folderResponse = await axios.get(`${apiUrl}/folders/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const isOwn = folderResponse.data.own_folders.some(folder =>
        folder.id === parseInt(subfolder_id) ||
        folder.subfolders?.some(sf => sf.id === parseInt(subfolder_id))
      );

      if (isOwn) {
        setFolderType('own');
        setIsAssignedFolder(false);
      } else {
        const isFree = folderResponse.data.assigned_folders.free.some(folder =>
          folder.id === parseInt(subfolder_id) ||
          folder.subfolders?.some(sf => sf.id === parseInt(subfolder_id))
        );

        if (isFree) {
          setFolderType('free');
          setIsAssignedFolder(true);
        } else {
          setFolderType('paid');
          setIsAssignedFolder(true);
        }
      }
    } catch (error) {
      console.error("Failed to initialize component:", error);
    }
  };

  initializeComponent();
}, [subfolder_id, apiUrl]);

  // ---- fetch saved questions ----
  useEffect(() => {
    if (subfolder_id) fetchSavedQuestions();
  }, [subfolder_id]);

const fetchSavedQuestions = async () => {
  setIsLoading(true);
  try {
    const response = await axios.get(
      `${apiUrl}/subfolder/${subfolder_id}/uploadimage/`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const formatted = response.data.map(q => ({
      ...q,
      labels:
        typeof q.labels === "string"
          ? JSON.parse(q.labels)
          : q.labels || []
    }));

    setSavedQuestions(formatted);
  } catch (error) {
    console.error('Error fetching saved questions:', error);
    setSnackbar({
      open: true,
      message: 'Failed to fetch saved questions',
      severity: 'error'
    });
  } finally {
    setIsLoading(false);
  }
};

  // ---- main canvas redraw (for create new question) ----
  useEffect(() => {
    if (imageDataUrl && originalImage) redrawCanvas();
  }, [imageDataUrl, originalImage, labels]);

 const redrawCanvas = () => {
  const canvas = canvasRef.current;
  const img = originalImage;
  if (!canvas || !img) return;

  const ctx = canvas.getContext("2d");

  const maxWidth = 800;
  const aspectRatio = img.width / img.height;

  canvas.width = Math.min(maxWidth, img.width);
  canvas.height = canvas.width / aspectRatio;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  labels.forEach((label, index) => {
    ctx.fillStyle = colors[index % colors.length];
    ctx.fillRect(label.x, label.y, label.width, label.height);

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 26px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillText(
      label.id,
      label.x + label.width / 2,
      label.y + label.height / 2
    );
  });
};

  const getNextId = () => String(labels.length + 1);

  // ---- create-mode mouse handlers (for new question) ----
  const handleMouseDown = (event) => {
    if (!imageDataUrl) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    setIsDrawing(true);
    setStartPoint({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  };

const handleMouseMove = (event) => {
  if (!isDrawing) return;

  const canvas = canvasRef.current;
  const ctx = canvas.getContext("2d");
  const rect = canvas.getBoundingClientRect();

  const currentX = event.clientX - rect.left;
  const currentY = event.clientY - rect.top;

  // Always redraw base image + saved labels
  redrawCanvas();

  // 🔥 Normalize rectangle
  const x = Math.min(startPoint.x, currentX);
  const y = Math.min(startPoint.y, currentY);
  const width = Math.abs(currentX - startPoint.x);
  const height = Math.abs(currentY - startPoint.y);

  const nextId = labels.length + 1;

  // Draw preview rectangle
  ctx.fillStyle = colors[(nextId - 1) % colors.length];
  ctx.fillRect(x, y, width, height);

  // Draw preview ID
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 26px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.fillText(
    nextId,
    x + width / 2,
    y + height / 2
  );
};

  const handleMouseUp = (event) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const endPoint = { x: event.clientX - rect.left, y: event.clientY - rect.top };

    const width = endPoint.x - startPoint.x;
    const height = endPoint.y - startPoint.y;

    if (Math.abs(width) > 20 && Math.abs(height) > 20) {
      const newLabel = {
        id: getNextId(),
        x: Math.min(startPoint.x, endPoint.x),
        y: Math.min(startPoint.y, endPoint.y),
        width: Math.abs(width),
        height: Math.abs(height),
        answer: '',
        explanation: ''
      };
      // open dialog to collect answer, but add to labels only when saved
      setCurrentLabel(newLabel);
      setEditingIndex(null);
      setDialogOpen(true);
    }
  };

  // ---- create-mode label handlers ----
 const handleSaveLabel = () => {
  if (!currentLabel.answer.trim()) {
    setSnackbar({ open: true, message: 'Answer is required!', severity: 'error' });
    return;
  }

  let updatedLabels;

  if (editingIndex !== null) {
    updatedLabels = [...labels];
    updatedLabels[editingIndex] = {
      ...updatedLabels[editingIndex],
      answer: currentLabel.answer,
      explanation: currentLabel.explanation
    };
  } else {
    updatedLabels = [...labels, { ...currentLabel }];
  }

  setLabels(updatedLabels);

  setDialogOpen(false);
  setCurrentLabel({ id: '', answer: '', explanation: '' });
  setEditingIndex(null);
 };
 
  const handleEditLabel = (index) => {
    setCurrentLabel(labels[index]);
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleDeleteLabel = (index) => {
    const updated = labels.filter((_, i) => i !== index).map((lab, i) => ({ ...lab, id: String(i + 1) }));
    setLabels(updated);
  };

  // ---- submit new question ----
  const handleSubmit = async () => {
    if (!imageFile) return setSnackbar({ open: true, message: 'Please upload an image!', severity: 'error' });
    if (!statement.trim()) return setSnackbar({ open: true, message: 'Please enter a statement/question!', severity: 'error' });
    if (labels.length === 0) return setSnackbar({ open: true, message: 'Please add at least one label!', severity: 'error' });

    const incompleteLabels = labels.filter(l => !l.answer.trim());
    if (incompleteLabels.length > 0) return setSnackbar({ open: true, message: 'All labels must have answers!', severity: 'error' });

    setIsSubmitting(true);
    try {
      const canvas = canvasRef.current;
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('statement', statement);
      formData.append('question_type', 'IMAGE');
      formData.append('folder', subfolder_id);
      formData.append('canvas_width', canvas.width);
      formData.append('canvas_height', canvas.height);
      formData.append('labels', JSON.stringify(labels.map(l => ({
        id: l.id, answer: l.answer, explanation: l.explanation || '', x: l.x, y: l.y, width: l.width, height: l.height
      }))));
      formData.append('tags', JSON.stringify(tags.map(name => ({ name }))));
      await axios.post(`${apiUrl}/subfolder/${subfolder_id}/uploaded_images/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });

      setSnackbar({ open: true, message: 'Question created successfully!', severity: 'success' });
      resetForm();
      fetchSavedQuestions();
    } catch (error) {
      console.error('Error creating question:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create question', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setImageFile(null);
    setImageDataUrl(null);
    setOriginalImage(null);
    setLabels([]);
    setStatement('');
    setTags([]);
    setTagsInput('');
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all labels?')) {
      setLabels([]);
      redrawCanvas();
    }
  };

  // ---- saved questions UI helpers ----
  const toggleCardExpand = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleReadOnlyCardExpand = (id) => setReadOnlyExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

  const openDeleteDialog = (id) => { setDeleteQuestionId(id); setDeleteDialogOpen(true); };

  const handleDeleteQuestion = async () => {
    try {
      await axios.delete(`${apiUrl}/subfolder/${subfolder_id}/uploaded_images/${deleteQuestionId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: 'Question deleted successfully!', severity: 'success' });
      setDeleteDialogOpen(false);
      fetchSavedQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      setSnackbar({ open: true, message: 'Failed to delete question', severity: 'error' });
    }
  };

  const openEditQuestionDialog = (question) => {
    setEditingQuestion(question ? { ...question } : null);
    setEditTagsInput(question?.tags?.map(t => t.name).join(', ') || '');
    setEditQuestionDialogOpen(true);
    setIsDrawing(false);
    editBaseImageRef.current = null;
  };

  // ---- EDIT MODE: preload image only once and draw efficiently (Option A: add label after Save) ----

  // preload image when editingQuestion changes
  // useEffect(() => {
  //   if (!editingQuestion?.file_url) return;
  //   const img = new Image();
  //   img.crossOrigin = 'anonymous';
  //   img.onload = () => {
  //     editBaseImageRef.current = img;
  //     // draw immediately after loading
  //     drawEditCanvas();
  //   };
  //   img.onerror = () => {
  //     console.error('Failed to load edit image:', editingQuestion.file_url);
  //   };
  //   img.src = editingQuestion.file_url;
  //   // redraw if labels change
  // }, [editingQuestion?.file_url, editingQuestion?.labels]);
  useEffect(() => {
    if (!editingQuestion?.file_url && !editImageDataUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      editBaseImageRef.current = img;
      drawEditCanvas();
    };
    img.onerror = () => {
      console.error('Failed to load edit image');
    };
    // Use the new image if available, otherwise use the original
    img.src = editImageDataUrl || `${apiUrl}${editingQuestion.file_url}`;
  }, [editingQuestion?.file_url, editingQuestion?.labels, editImageDataUrl]);


  const drawEditCanvas = () => {
    const canvas = editCanvasRef.current;
    const img = editBaseImageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    const maxWidth = 800;
    const ratio = img.width / img.height;
    canvas.width = Math.min(maxWidth, img.width);
    canvas.height = canvas.width / ratio;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    // Draw labels
    if (editingQuestion?.labels) {
      editingQuestion.labels.forEach((label, index) => {
        ctx.fillStyle = colors[index % colors.length];
        ctx.fillRect(label.x, label.y, label.width, label.height);
        ctx.fillStyle = '#ffffff';
ctx.font = 'bold 26px Arial';
ctx.textAlign = 'center';
ctx.textBaseline = 'middle';

ctx.fillText(
  label.id,
  label.x + label.width / 2,
  label.y + label.height / 2
);
        
      });
    }
  };


  // edit-mode drawing state is shared with main component states (isDrawing, startPoint)
  const handleEditCanvasMouseDown = (event) => {
    const canvas = editCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    setIsDrawing(true);
    setStartPoint({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  };

  const handleEditCanvasMouseMove = (event) => {
    if (!isDrawing) return;
    const canvas = editCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const currentPoint = { x: event.clientX - rect.left, y: event.clientY - rect.top };

    // redraw canvas from cached image and existing labels (fast)
    drawEditCanvas();

    // draw preview rectangle only (not mutating editingQuestion.labels)
    const width = currentPoint.x - startPoint.x;
    const height = currentPoint.y - startPoint.y;
    const nextId = (editingQuestion?.labels?.length || 0) + 1;
    ctx.fillStyle = colors[(nextId - 1) % colors.length];
    ctx.fillRect(startPoint.x, startPoint.y, width, height);
ctx.fillStyle = '#ffffff';
ctx.font = 'bold 26px Arial';
ctx.textAlign = 'center';
ctx.textBaseline = 'middle';

ctx.fillText(
  nextId,
  startPoint.x + width / 2,
  startPoint.y + height / 2
);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
  };

  const handleEditCanvasMouseUp = (event) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = editCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const endPoint = { x: event.clientX - rect.left, y: event.clientY - rect.top };

    const width = endPoint.x - startPoint.x;
    const height = endPoint.y - startPoint.y;

    if (Math.abs(width) <= 20 || Math.abs(height) <= 20) {
      // too small, ignore
      drawEditCanvas();
      return;
    }

    // prepare new label but DO NOT add to editingQuestion.labels yet (Option A)
    const newLabel = {
      id: String((editingQuestion?.labels?.length || 0) + 1),
      x: Math.min(startPoint.x, endPoint.x),
      y: Math.min(startPoint.y, endPoint.y),
      width: Math.abs(width),
      height: Math.abs(height),
      answer: '',
      explanation: ''
    };

    setCurrentLabel(newLabel);
    // set a sentinel editingIndex to indicate "new label" (we'll treat null as new)
    setEditingIndex(null);
    setDialogOpen(true);
  };

  // Save label from edit dialog (Option A: add only when saved)
  const handleSaveLabelInEdit = () => {
    if (!currentLabel.answer.trim()) {
      setSnackbar({ open: true, message: 'Answer is required!', severity: 'error' });
      return;
    }
    setEditingQuestion(prev => {
      if (!prev) return prev;
      const labelsArr = Array.isArray(prev.labels) ? [...prev.labels] : [];
      if (editingIndex !== null && editingIndex < labelsArr.length) {
        labelsArr[editingIndex] = { ...labelsArr[editingIndex], answer: currentLabel.answer, explanation: currentLabel.explanation };
      } else {
        labelsArr.push({ ...currentLabel });
      }
      return { ...prev, labels: labelsArr };
    });
    setDialogOpen(false);
    setCurrentLabel({ id: '', answer: '', explanation: '' });
    setEditingIndex(null);
    // Force redraw
    
  };

  // Delete label in edit mode
  const handleDeleteLabelInEdit = (index) => {
    setEditingQuestion(prev => {
      if (!prev) return prev;
      const updated = prev.labels.filter((_, i) => i !== index).map((lab, i) => ({ ...lab, id: String(i + 1) }));
      return { ...prev, labels: updated };
    });
    
  };

  // Update question back to server
  const handleUpdateQuestion = async () => {
    try {
      const formData = new FormData();
      formData.append('statement', editingQuestion.statement);
      formData.append('labels', JSON.stringify((editingQuestion.labels || []).map(l => ({
        id: l.id,
        answer: l.answer,
        explanation: l.explanation || '',
        x: l.x,
        y: l.y,
        width: l.width,
        height: l.height
      }))));
      formData.append('question_type', 'IMAGE');
      formData.append('tags', JSON.stringify((editingQuestion.tags || []).map(tag => ({ name: tag.name }))));
      // Append the new image if it exists
      if (editImageFile) {
        formData.append('image', editImageFile);
      }
      await axios.put(
        `${apiUrl}/subfolder/${subfolder_id}/uploaded_images/${editingQuestion.id}/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setSnackbar({ open: true, message: 'Question updated successfully!', severity: 'success' });
      setEditQuestionDialogOpen(false);
      fetchSavedQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.labels?.[0] || 'Failed to update question',
        severity: 'error'
      });
    }
  };


useEffect(() => {
  if (!previewDialogOpen || !previewQuestion) return;

  // small delay to ensure dialog + canvas is mounted
  const timer = setTimeout(() => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";

    const imageUrl = previewQuestion.file_url?.startsWith("http")
      ? previewQuestion.file_url
      : `${apiUrl}${previewQuestion.file_url}`;

    img.onload = () => {
      const maxWidth = 800;
      const aspectRatio = img.width / img.height;

      canvas.width = Math.min(maxWidth, img.width);
      canvas.height = canvas.width / aspectRatio;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      if (previewQuestion.labels) {
        previewQuestion.labels.forEach((label, index) => {
          const isRevealed = index < revealedCount;

          if (!isRevealed) {
            ctx.fillStyle = colors[index % colors.length];
            ctx.fillRect(label.x, label.y, label.width, label.height);

            ctx.fillStyle = "#ffffff";
            ctx.font = "bold 26px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            ctx.fillText(
              label.id,
              label.x + label.width / 2,
              label.y + label.height / 2
            );
          } else {
            ctx.fillStyle = "rgba(255,255,255,0.85)";
            ctx.fillRect(label.x, label.y, label.width, label.height);

            ctx.fillStyle = "#000000";
            ctx.font = "bold 18px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            ctx.fillText(
              label.answer || "",
              label.x + label.width / 2,
              label.y + label.height / 2
            );
          }
        });
      }
    };

    img.src = imageUrl;
  }, 1); // <-- important delay

  return () => clearTimeout(timer);

}, [previewDialogOpen, previewQuestion, revealedCount]);

return (
    isLoading ? (
  <Box sx={{ p: 2 }}>
    {[...Array(4)].map((_, i) => (
      <Box
        key={i}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: "16px",
          border: mode === "dark" ? "1px solid #333" : "1px solid #e0e7ff",
          backgroundColor: mode === "dark" ? "#2A2A3E" : "#fff",
        }}
      >
        {/* Question row */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Skeleton
            variant="rectangular"
            width={45}
            height={40}
            sx={{ borderRadius: "10px", mr: 2 }}
          />
          <Skeleton variant="text" width="70%" height={28} />
        </Box>
        {/* Tag chips */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={60} height={22} />
          <Skeleton variant="rounded" width={80} height={22} />
          <Skeleton variant="rounded" width={50} height={22} />
        </Box>
      </Box>
    ))}
  </Box>
) : (
    <>
   {!isAssignedFolder ? (
    <>
    <Box sx={{
      // p: 3,
      // maxWidth: 1200,
      // mx: 'auto',
      // backgroundColor: mode === 'dark' ? '#121212' : '#f8f9ff',
      // borderRadius: '24px',
      // boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
    }}>


      <div style={{ position: 'relative', marginBottom: '12px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: '8px',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 3,
              fontWeight: '800',
              background: mode === "dark"
                ? "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)"
                : mode === "custom"
                  ? "#f5f5f5" || "#222222"
                  : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: 'text'
            }}
          >
            Image Occlusion Questions
          </Typography>

          <Tooltip title="User Guide">
            <IconButton
              onClick={() => setUserGuideOpen(true)}
              sx={{
                color: mode === "dark" ? "#90caf9" : "#1e8bafff",
                backgroundColor: mode === "dark"
                  ? "rgba(144, 202, 249, 0.1)"
                  : "rgba(30, 139, 175, 0.1)",
                "&:hover": {
                  backgroundColor: mode === "dark"
                    ? "rgba(144, 202, 249, 0.2)"
                    : "rgba(30, 139, 175, 0.2)",
                },
                '&:focus': { outline: 'none' }
              }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {/* Create New Question */}
      <Card sx={{
        mb: 3,
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(102, 126, 234, 0.2)',
        },
        backgroundColor: mode === 'dark' ? '#1E1E2E' : '#fff',
        border: '1px solid',
        borderColor: mode === 'dark' ? '#333' : '#e0e7ff',
      }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#000' }}>
            Create New Question
          </Typography>

          <Typography variant="h6" sx={{ mb: 2 }}>Step 1: Upload Image</Typography>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #667eea',
              borderRadius: '12px',
              padding: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: mode === 'dark' ? '#2A2A3E' : '#f9f9f9',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: mode === 'dark' ? '#3A3A5E' : '#f0f0f0',
                borderColor: '#764ba2',
              },
              mb: 3,
            }}
          >
            <input {...getInputProps()} />
            <Typography>
              <i className="bi bi-cloud-arrow-up" style={{ color: '#667eea', marginRight: '8px' }}></i>
              {imageFile ? imageFile.name : 'Drag & drop an image here, or click to select'}
            </Typography>
          </Box>

          {imageDataUrl && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>Step 2: Enter Question Statement</Typography>
              <TextField fullWidth label="Question Statement" placeholder="e.g., Label the states on the map of India"
                value={statement} onChange={(e) => setStatement(e.target.value)} multiline rows={2} variant="outlined" sx={{ mb: 3 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>Step 3: Add Tags (Optional)</Typography>
              <TextField
                fullWidth
                label="Tags (comma-separated, e.g., Programming, IMAGES)"
                placeholder="e.g., Programming, IMAGES"
                value={tagsInput}
                onChange={(e) => {
                  const input = e.target.value;
                  setTagsInput(input);
                  const newTags = input.split(',').map(tag => tag.trim());
                  setTags(newTags);
                }}
                variant="outlined"
                sx={{ mb: 3 }}
              />

              <Typography variant="h6" sx={{ mb: 2 }}>Step 3: Draw Boxes on Labels</Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Click and drag to draw a box over each label you want to hide. Each box will be assigned an ID automatically.
              </Typography>

              <Box sx={{ border: '1px solid #cccccc', borderRadius: 1, overflow: 'hidden', display: 'inline-block', mb: 3 }}>
                <canvas ref={canvasRef} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}
                  style={{ cursor: 'crosshair', display: 'block' }} />
              </Box>

              {labels.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Step 4: Review Labels ({labels.length})</Typography>
                    <Button variant="outlined" color="error" size="small" onClick={handleClearAll}>Clear All</Button>
                  </Box>

                 <Box
  sx={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1.5,
    mb: 3,
  }}
>
  {labels.map((label, index) => (
    <Chip
      key={index}
      label={`${label.id}: ${label.answer || 'No answer'}`}
      onDelete={() => handleDeleteLabel(index)}
      onClick={() => handleEditLabel(index)}
      deleteIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
      sx={{
        cursor: 'pointer',
        fontWeight: 600,
        borderRadius: '20px',
        px: 1.5,
        height: 36,
        backgroundColor: label.answer ? '#1e3a8a' : '#e5e7eb',
        color: label.answer ? '#fff' : '#333',
        '& .MuiChip-deleteIcon': {
          color: 'inherit',
        },
        '&:hover': {
          opacity: 0.9,
        }
      }}
    />
  ))}
</Box>
                </>
              )}

              <Box
                sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
              >

                <Button
                  sx={{
                    color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.primary : "#142be1",
                    borderColor: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#142be1",
                    fontSize: "0.85rem",
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: mode === "dark" ? "#ccc" : mode === "custom" ? customColors.text : "#0d1abf",
                      backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : mode === "custom" ? "rgba(20,43,225,0.08)" : "rgba(20,43,225,0.08)"
                    }
                  }}
                  variant="outlined"
                  onClick={resetForm} disabled={isSubmitting}>Cancel</Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  //  sx={{
                  //   background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                  //   color: '#fff',
                  //   '&:hover': {
                  //     background: 'linear-gradient(135deg, #1976D2 0%, #2196F3 100%)',
                  //   },
                  //     '&:disabled': {
                  //       background: '#d1d5db',
                  //       color: '#fff',
                  //       opacity: 0.6,
                  //     },
                  //     padding: '10px 24px',
                  //     fontWeight: '600',
                  //     borderRadius: '8px',
                  //   }}
                  sx={{
                    background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                    color: "white",
                    padding: "6px 16px",     // reduced size
                    fontSize: "0.85rem",     // smaller text
                    fontWeight: "700",
                    borderRadius: "8px",     // slightly smaller radius
                    textTransform: "none",
                    boxShadow: "0 8px 25px rgba(30, 58, 138, 0.4)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
                      transform: "translateY(-3px)",
                      boxShadow: "0 12px 35px rgba(30, 58, 138, 0.5)",
                    },
                    "&.Mui-disabled": {
                      background: "#d1d5db",
                      color: "white",
                      opacity: 0.6,
                    },
                  }}
                >
                  {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Create Question'}
                </Button>

              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Saved Questions */}
      <Card sx={{
        mb: 3,
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(102, 126, 234, 0.2)',
        },
        backgroundColor: mode === 'dark' ? '#1E1E2E' : '#fff',
        border: '1px solid',
        borderColor: mode === 'dark' ? '#333' : '#e0e7ff',
      }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#000' }}>
            Saved Questions ({savedQuestions.length})
          </Typography>

          {savedQuestions.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              p: 6,
              backgroundColor: mode === 'dark' ? '#2A2A3E' : '#f0f4ff',
              borderRadius: '16px',
              border: '2px dashed',
              borderColor: mode === 'dark' ? '#555' : '#c7d2fe',
            }}>
              <i className="bi bi-inbox" style={{
                fontSize: '3rem',
                color: mode === 'dark' ? '#667eea' : '#c7d2fe',
                marginBottom: '16px',
              }}></i>
              <Typography sx={{ fontSize: '1.2rem', color: mode === 'dark' ? '#e0e0e0' : '#666' }}>
                No questions created yet. Create your first one above!
              </Typography>
            </Box>
          ) : (
            savedQuestions.map((question, index) => (
              <Card key={question.id} sx={{
                mb: 2,
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(102, 126, 234, 0.2)',
                  transform: 'translateY(-4px)',
                },
                backgroundColor: mode === 'dark' ? '#2A2A3E' : '#fff',
                border: '1px solid',
                borderColor: mode === 'dark' ? '#333' : '#e0e7ff',
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, pr: 5 }}>
                      <Typography variant="h6" sx={{ mb: 1, color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#000' }}>
                        <strong>Q:{index + 1}</strong> {question.statement}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Labels: {question.labels?.length || 0}
                      </Typography>
                      {question.tags && question.tags.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {question.tags.map((tag, index) => (
                            <Chip key={index} label={`#${tag.name}`} size="small" />
                          ))}
                        </Box>
                      )}
                    </Box>


                  <Box sx={{ display: 'flex', gap: '8px', padding: '8px 12px' }}>

  {/* Expand */}
  <IconButton
    size="small"
    onClick={() => toggleCardExpand(question.id)}
    title={expandedCards[question.id] ? "Collapse" : "Expand"}
    sx={{
      color: '#667eea',
      '&:hover': { color: '#764ba2' },
      '&:focus': { outline: 'none' },
    }}
  >
    <i className={`bi ${
      expandedCards[question.id]
        ? 'bi-arrows-angle-contract'
        : 'bi-arrows-angle-expand'
    }`}></i>
  </IconButton>

  {/* 👁 Eye */}
  <IconButton
    size="small"
    onClick={() => openPreviewDialog(question)}
    title="Preview"
    sx={{
      color: '#3b82f6',
      '&:hover': { color: '#2563eb' },
      '&:focus': { outline: 'none' },
    }}
  >
    <VisibilityIcon fontSize="small" />
  </IconButton>

  {/* Edit */}
  <IconButton
    size="small"
    onClick={() => openEditQuestionDialog(question)}
    title="Edit"
    sx={{
      color: '#10b981',
      '&:hover': { color: '#059669' },
      '&:focus': { outline: 'none' },
    }}
  >
    <EditIcon />
  </IconButton>

  {/* Delete */}
  <IconButton
    size="small"
    onClick={() => openDeleteDialog(question.id)}
    title="Delete"
    sx={{
      color: '#ef4444',
      '&:hover': { color: '#dc2626' },
      '&:focus': { outline: 'none' },
    }}
  >
    <DeleteIcon />
  </IconButton>

</Box>

                  </Box>

                  {expandedCards[question.id] && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ddd' }}>
                      {question.file_url && (
                        <Box sx={{ textAlign: 'center', my: 2 }}>
                          <canvas style={{ maxWidth: '100%', border: '1px solid #ccc', borderRadius: 8, display: 'block', margin: '0 auto' }}
                            ref={el => {
                              if (!el) return;
                              const ctx = el.getContext('2d');
                              const img = new Image();
                              img.crossOrigin = 'anonymous';
                              img.onload = () => {
                                const maxWidth = 800;
                                const aspectRatio = img.width / img.height;
                                el.width = Math.min(maxWidth, img.width);
                                el.height = el.width / aspectRatio;
                                ctx.drawImage(img, 0, 0, el.width, el.height);
                                (question.labels || []).forEach((label, index) => {
                                  ctx.fillStyle = colors[index % colors.length];
                                  ctx.fillRect(label.x, label.y, label.width, label.height);
                                  
                                 ctx.fillStyle = '#ffffff';
ctx.font = 'bold 26px Arial';
ctx.textAlign = 'center';
ctx.textBaseline = 'middle';

ctx.fillText(
  label.id,
  label.x + label.width / 2,
  label.y + label.height / 2
);
                                });
                              };
                              img.src = question.file_url?.startsWith("http")
  ? question.file_url
  : `${apiUrl}${question.file_url}`;
                            }} />
                        </Box>
                      )}

                      <Typography variant="subtitle1" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>Labels:</Typography>
                      {question.labels && question.labels.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {question.labels.map((label, index) => (
                            <Card key={index} sx={{ p: 2, backgroundColor: mode === 'dark' ? '#1E1E2E' : '#fff' }}>
                              <Typography variant="body1"><strong>ID {label.id}:</strong> {label.answer}</Typography>
                              {label.explanation && <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}><strong>Explanation:</strong> {label.explanation}</Typography>}
                            </Card>
                          ))}
                        </Box>
                      ) : (<Typography sx={{ color: '#666' }}>No labels available</Typography>)}

                      {/* {question.tags && question.tags.length > 0 && (
  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
    {question.tags.map((tag, index) => (
      <Chip key={index} label={`#${tag.name}`} size="small" />
    ))}
  </Box>
)} */}

                    </Box>
                  )}
                </CardContent>
              </Card>
            )))}

        </CardContent>
      </Card>

      {/* Label Dialog (used for both create-mode and edit-mode) */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIndex !== null ? `Edit Label ${currentLabel.id}` : `Add Label ${currentLabel.id}`}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField fullWidth label="Answer *" placeholder="e.g., Rajasthan"
              value={currentLabel.answer} onChange={(e) => setCurrentLabel({ ...currentLabel, answer: e.target.value })} sx={{ mb: 2 }} autoFocus />
            <TextField fullWidth label="Explanation (Optional)" placeholder="e.g., Largest state in India by area"
              value={currentLabel.explanation} onChange={(e) => setCurrentLabel({ ...currentLabel, explanation: e.target.value })} multiline rows={3} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {/* decide save handler depending on whether editDialog is open (editingQuestion) */}
          <Button onClick={() => {
            if (editQuestionDialogOpen) {
              handleSaveLabelInEdit();
            } else {
              handleSaveLabel();
            }
          }} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>



      {/* Edit Question Dialog */}
    <Dialog 
  open={editQuestionDialogOpen} 
  onClose={() => {
    setEditQuestionDialogOpen(false);
    setEditImageFile(null);
    setEditImageDataUrl(null);
  }} 
  maxWidth="lg" 
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: '16px',
      background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.cardBg : '#ffffff',
      boxShadow: mode === 'dark' 
        ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
        : '0 8px 32px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
    }
  }}
>
  {/* Gradient Header */}
  <Box sx={{
    background: mode === 'dark' 
      ? 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)'
      : mode === 'custom' 
        ? customColors.gradient || 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)'
        : 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
    p: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'white',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <i className="bi bi-pencil-square" style={{ fontSize: 28 }}></i>
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
          Edit Question
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Update your image occlusion question
        </Typography>
      </Box>
    </Box>
    {/* <IconButton 
      onClick={() => {
        setEditQuestionDialogOpen(false);
        setEditImageFile(null);
        setEditImageDataUrl(null);
      }}
      sx={{ 
        color: 'white',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
      }}
    >
      <CloseIcon />
    </IconButton> */}
  </Box>

  {/* Content */}
  <DialogContent sx={{ 
    p: 4,
    background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.background : '#ffffff',
    maxHeight: '70vh',
    overflowY: 'auto',
  "&::-webkit-scrollbar": {
      width: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: mode === "dark" ? "#2d3748" : "#f1f5f9",
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: mode === "dark" ? "#475569" : "#94a3b8",
      borderRadius: "6px",
      border: `2px solid ${mode === "dark" ? "#2d3748" : "#f1f5f9"}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#64748b",
    },
    scrollbarWidth: "thin",
    scrollbarColor: mode === "dark"
      ? "#475569 #2d3748"
      : "#94a3b8 #f1f5f9",
  }}
>
    {/* Replace Image Section */}
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 1.5, 
          fontWeight: 600,
          color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        }} />
        Replace Image
      </Typography>
      <input
        type="file"
        id="edit-image-upload"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            setEditImageFile(file);
            const reader = new FileReader();
            reader.onload = (event) => {
              setEditImageDataUrl(event.target.result);
              setEditingQuestion(prev => ({ ...prev, file_url: event.target.result }));
            };
            reader.readAsDataURL(file);
          }
        }}
      />
      <label htmlFor="edit-image-upload">
        <Button 
          variant="outlined" 
          component="span" 
          startIcon={<i className="bi bi-cloud-upload"></i>}
          sx={{
            borderColor: mode === 'dark' ? '#667eea' : mode === 'custom' ? customColors.primary : '#667eea',
            color: mode === 'dark' ? '#667eea' : mode === 'custom' ? customColors.primary : '#667eea',
            '&:hover': {
              borderColor: mode === 'dark' ? '#764ba2' : mode === 'custom' ? customColors.primaryHover : '#764ba2',
              backgroundColor: 'rgba(102, 126, 234, 0.05)',
            }
          }}
        >
          Replace Image
        </Button>
      </label>
    </Box>

    {/* Tags Section */}
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 1.5, 
          fontWeight: 600,
          color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        }} />
        Tags
      </Typography>
      <TextField
        fullWidth
        label="Tags (comma-separated)"
        placeholder="e.g., Geography, India, Maps"
        value={editTagsInput}
        onChange={(e) => {
          const input = e.target.value;
          setEditTagsInput(input);
          const newTags = input.split(',').map(tag => tag.trim());
          setEditingQuestion(prev => ({
            ...prev,
            tags: newTags.map(name => ({ name }))
          }));
        }}
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: mode === 'dark' ? '#555' : '#e0e7ff',
            },
            '&:hover fieldset': {
              borderColor: mode === 'dark' ? '#667eea' : '#667eea',
            },
          },
          '& .MuiInputLabel-root': {
            color: mode === 'dark' ? '#A0A0A6' : '#666',
          },
          '& .MuiInputBase-input': {
            color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#000',
          }
        }}
      />
    </Box>

    {/* Question Statement Section */}
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 1.5, 
          fontWeight: 600,
          color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
        }} />
        Question Statement <span style={{ color: '#ef4444' }}>*</span>
      </Typography>
      <TextField 
        fullWidth 
        label="Question Statement" 
        value={editingQuestion?.statement || ''} 
        onChange={(e) => setEditingQuestion(prev => ({ ...prev, statement: e.target.value }))} 
        multiline 
        rows={2} 
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: mode === 'dark' ? '#555' : '#e0e7ff',
            },
            '&:hover fieldset': {
              borderColor: mode === 'dark' ? '#667eea' : '#667eea',
            },
          },
          '& .MuiInputLabel-root': {
            color: mode === 'dark' ? '#A0A0A6' : '#666',
          },
          '& .MuiInputBase-input': {
            color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#000',
          }
        }}
      />
    </Box>

    {/* Canvas Section */}
    {editingQuestion?.file_url && (
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            mb: 1.5, 
            fontWeight: 600,
            color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          }} />
          Image with Labels
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 2, color: mode === 'dark' ? '#A0A0A6' : '#666' }}>
          Click and drag to add new labels
        </Typography>
        <Box sx={{ textAlign: 'center', my: 2 }}>
          <canvas
            ref={editCanvasRef}
            id="edit-canvas"
            onMouseDown={handleEditCanvasMouseDown}
            onMouseMove={handleEditCanvasMouseMove}
            onMouseUp={handleEditCanvasMouseUp}
            style={{ 
              maxWidth: '100%', 
              border: '2px solid #667eea', 
              borderRadius: 12, 
              display: 'block', 
              margin: '0 auto', 
              cursor: 'crosshair',
              backgroundColor: mode === 'dark' ? '#2A2A3E' : '#f9f9f9',
            }}
          />
        </Box>
      </Box>
    )}

    {/* Labels Section */}
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600,
            color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
          }}
        >
          Labels ({editingQuestion?.labels?.length || 0})
        </Typography>
        <Typography variant="caption" sx={{ color: mode === 'dark' ? '#A0A0A6' : '#666' }}>
          Click on a label to edit, or click delete icon to remove
        </Typography>
      </Box>

      {editingQuestion?.labels?.map((label, index) => (
        <Card key={index} sx={{
          mb: 2, 
          p: 2, 
          border: '2px solid', 
          borderColor: colors[index % colors.length].replace('0.6', '1'),
          backgroundColor: mode === 'dark' ? '#2A2A3E' : '#f9f9f9',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': { 
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          }
        }} 
        onClick={() => { 
          setCurrentLabel(label); 
          setEditingIndex(index); 
          setDialogOpen(true); 
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 'bold', 
                  color: colors[index % colors.length].replace('0.6', '1') 
                }}
              >
                Label ID: {label.id}
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5, color: mode === 'dark' ? '#E0E0E6' : '#000' }}>
                <strong>Answer:</strong> {label.answer || '(No answer)'}
              </Typography>
              {label.explanation && (
                <Typography variant="body2" sx={{ color: mode === 'dark' ? '#A0A0A6' : '#666', mb: 0.5 }}>
                  <strong>Explanation:</strong> {label.explanation}
                </Typography>
              )}
            </Box>
            <IconButton 
              size="small" 
              sx={{
                color: '#ef4444',
                '&:hover': {
                  color: '#dc2626',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                }
              }}
              onClick={(e) => { 
                e.stopPropagation(); 
                handleDeleteLabelInEdit(index); 
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Card>
      ))}

      {(!editingQuestion?.labels || editingQuestion.labels.length === 0) && (
        <Box sx={{
          textAlign: 'center',
          p: 4,
          backgroundColor: mode === 'dark' ? '#2A2A3E' : '#f0f4ff',
          borderRadius: '12px',
          border: '2px dashed',
          borderColor: mode === 'dark' ? '#555' : '#c7d2fe',
        }}>
          <i className="bi bi-bounding-box" style={{
            fontSize: '2rem',
            color: mode === 'dark' ? '#667eea' : '#c7d2fe',
            marginBottom: '8px',
          }}></i>
          <Typography sx={{ color: mode === 'dark' ? '#A0A0A6' : '#666' }}>
            No labels yet. Draw on the image above to add labels.
          </Typography>
        </Box>
      )}
    </Box>
  </DialogContent>

  {/* Divider */}
  <Box sx={{ 
    height: '1px', 
    backgroundColor: mode === 'dark' ? '#333' : mode === 'custom' ? customColors.border : '#e5e7eb' 
  }} />

  {/* Actions */}
  <DialogActions sx={{ 
    p: 3, 
    gap: 2,
    background: mode === 'dark' ? '#2A2A3E' : mode === 'custom' ? customColors.background : '#f8f9fa',
  }}>
    <Button 
      onClick={() => {
        setEditQuestionDialogOpen(false);
        setEditImageFile(null);
        setEditImageDataUrl(null);
      }}
      variant="outlined"
      sx={{ 
        borderColor: mode === 'dark' ? '#555' : mode === 'custom' ? customColors.border : '#d1d5db',
        color: mode === 'dark' ? '#A0A0A6' : mode === 'custom' ? customColors.textSecondary : '#6B7280',
        '&:hover': {
          borderColor: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
          background: 'transparent',
        }
      }}
    >
      Cancel
    </Button>
    <Button 
      onClick={handleUpdateQuestion}
      variant="contained"
      sx={{ 
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
        color: 'white',
        fontWeight: 600,
        px: 4,
        '&:hover': {
          background: 'linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(30, 58, 138, 0.3)',
        },
        transition: 'all 0.3s ease',
      }}
    >
      Update Question
    </Button>
  </DialogActions>
</Dialog>

      {/* Delete Dialog */}
      <Dialog 
  open={deleteDialogOpen} 
  onClose={() => setDeleteDialogOpen(false)}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: '20px',
      background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.cardBg : '#ffffff',
      boxShadow: mode === 'dark' 
        ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
        : '0 8px 32px rgba(239, 68, 68, 0.2)',
      overflow: 'hidden',
    }
  }}
>
  {/* Warning Header */}
  <Box sx={{
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    p: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'white',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* <i className="bi bi-exclamation-triangle" style={{ fontSize: 28 }}></i> */}
        <i className="bi bi-trash3" style={{
          fontSize: 40,
          // color: '#ef4444',
        }}></i>

      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
          Delete Question
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          This action cannot be undone
        </Typography>
      </Box>
    </Box>
    {/* <IconButton 
      onClick={() => setDeleteDialogOpen(false)}
      sx={{ 
        color: 'white',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
      }}
    >
      <CloseIcon />
    </IconButton> */}
  </Box>

  {/* Content */}
  <DialogContent sx={{ 
    p: 4,
    background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.background : '#ffffff',
  }}>
    <Box sx={{
      textAlign: 'center',
      py: 2,
    }}>
      {/* <Box sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: mode === 'dark' 
          ? 'rgba(239, 68, 68, 0.1)' 
          : 'rgba(239, 68, 68, 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
      
      </Box> */}
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2, 
          color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
          fontWeight: 600,
        }}
      >
        Are you sure you want to delete this question?
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: mode === 'dark' ? '#A0A0A6' : mode === 'custom' ? customColors.textSecondary : '#6B7280',
          lineHeight: 1.6,
        }}
      >
        This will permanently remove the question, image, and all associated labels. This action cannot be reversed.
      </Typography>
    </Box>
  </DialogContent>

  {/* Divider */}
  <Box sx={{ 
    height: '1px', 
    backgroundColor: mode === 'dark' ? '#333' : mode === 'custom' ? customColors.border : '#e5e7eb' 
  }} />

  {/* Actions */}
  <DialogActions sx={{ 
    p: 3, 
    gap: 2,
    background: mode === 'dark' ? '#2A2A3E' : mode === 'custom' ? customColors.background : '#f8f9fa',
  }}>
    <Button 
      onClick={() => setDeleteDialogOpen(false)}
      variant="outlined"
      fullWidth
      sx={{ 
        borderColor: mode === 'dark' ? '#555' : mode === 'custom' ? customColors.border : '#d1d5db',
        color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
        fontWeight: 600,
        '&:hover': {
          borderColor: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#1E1B18',
          background: 'transparent',
        }
      }}
    >
      Cancel
    </Button>
    <Button 
      onClick={handleDeleteQuestion}
      variant="contained"
      fullWidth
      sx={{ 
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white',
        fontWeight: 600,
        '&:hover': {
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(239, 68, 68, 0.4)',
        },
        transition: 'all 0.3s ease',
      }}
    >
      Delete Question
    </Button>
  </DialogActions>
</Dialog>

<Dialog
  open={previewDialogOpen}
  onClose={() => setPreviewDialogOpen(false)}
  maxWidth="md"
  fullWidth
  TransitionProps={{
    onEntered: () => {
      setRevealedCount(0);   // reset when dialog fully opens
    }
  }}
  PaperProps={{
    sx: {
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
    }
  }}
>
  <Box sx={{ minHeight: 520, display: "flex", flexDirection: "column", p: { xs: 2, sm: 4 } }}>
    {/* Header */}
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, px: 2 }}>
      <Box sx={{
        backgroundColor: "rgba(255,255,255,0.2)",
        color: "#fff",
        padding: "8px 24px",
        borderRadius: 20,
        fontWeight: "bold",
        fontSize: "0.95rem",
      }}>
        Image Occlusion Preview
      </Box>
      <IconButton
        onClick={() => setPreviewDialogOpen(false)}
        sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" } }}
      >
        <CloseIcon fontSize="large" />
      </IconButton>
    </Box>

    {/* White Content Area */}
    <Box sx={{
      backgroundColor: "#ffffff",
      borderRadius: 16,
      p: { xs: 3, sm: 5 },
      flex: 1,
      display: "flex",
      flexDirection: "column",
      boxShadow: "inset 0 2px 12px rgba(0,0,0,0.08)",
      color: "#000000",
      overflowY: "auto",
      maxHeight: "75vh",
    }}>
      {/* Question Statement */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 5, lineHeight: 1.5, textAlign: "center" }}>
        {previewQuestion?.statement}
      </Typography>

     {/* Image with Hidden Labels (Preview Mode) */}
{previewQuestion?.file_url && (
  <Box sx={{ textAlign: "center", my: 2 }}>
    <canvas
      ref={previewCanvasRef}
      style={{
        maxWidth: "100%",
        border: "1px solid #ccc",
        borderRadius: 8,
        display: "block",
        margin: "0 auto"
      }}
    />
  </Box>
)}

      {/* Show Answer Button */}
      {previewQuestion?.labels && revealedCount < previewQuestion.labels.length && (
  <Box sx={{ mt: "auto", display: "flex", justifyContent: "center" }}>
    <Button
      onClick={() => setRevealedCount(prev => prev + 1)}
      sx={{
        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
        color: "#fff",
        padding: "10px 32px",
        borderRadius: 30,
        fontWeight: "bold",
        textTransform: "none",
      }}
    >
      Reveal Next ({revealedCount + 1}/{previewQuestion.labels.length})
    </Button>
  </Box>
)}
         

      {/* Answers Section (Visible after clicking Show Answers) */}
     {revealedCount > 0 && (
        <Box sx={{ mt: 4, pt: 3, borderTop: "2px solid #e0e0e0" }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Answers:</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {previewQuestion?.labels
  ?.slice(0, revealedCount)
  .map((label, index) => (
              <Card key={index} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                <Typography variant="body1"><strong>ID {label.id}:</strong> {label.answer}</Typography>
                {label.explanation && (
                  <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                    <strong>Explanation:</strong> {label.explanation}
                  </Typography>
                )}
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  </Box>
</Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
   <Dialog
  open={userGuideOpen}
  onClose={() => setUserGuideOpen(false)}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: "16px",
      boxShadow: mode === "dark"
        ? "0 8px 32px rgba(0, 0, 0, 0.4)"
        : "0 8px 32px rgba(30, 139, 175, 0.15)",
      overflow: "hidden",
      background: mode === "dark"
        ? "#1E1B18"
        : mode === "custom"
          ? customColors.bg
          : "#ffffff",
    },
  }}
>
  {/* Header */}
  <Box
    sx={{
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      p: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: "white",
    }}
  >
    <Box>
      <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
        User Guide
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        Image Occlusion Questions
      </Typography>
    </Box>
    <IconButton
      onClick={() => setUserGuideOpen(false)}
      sx={{
        color: "white",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
      }}
    >
      <CloseIcon />
    </IconButton>
  </Box>

  {/* Content */}
  <DialogContent
    sx={{
      p: 4,
      overflowY: "auto",
      maxHeight: "75vh",
      "&::-webkit-scrollbar": {
        width: "10px",
      },
      "&::-webkit-scrollbar-track": {
        background: mode === "dark"
          ? "#2d3748"
          : mode === "custom"
            ? customColors.scrollTrack
            : "#f1f5f9",
        borderRadius: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: mode === "dark"
          ? "#475569"
          : mode === "custom"
            ? customColors.scrollThumb
            : "#94a3b8",
        borderRadius: "6px",
        border: `2px solid ${
          mode === "dark"
            ? "#2d3748"
            : mode === "custom"
              ? customColors.border
              : "#f1f5f9"
        }`,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: mode === "dark"
          ? "#64748b"
          : mode === "custom"
            ? customColors.scrollThumbHover
            : "#475569",
      },
      scrollbarWidth: "thin",
      scrollbarColor: mode === "dark"
        ? "#475569 #2d3748"
        : mode === "custom"
          ? `${customColors.scrollThumb} ${customColors.scrollTrack}`
          : "#94a3b8 #f1f5f9",
      background: mode === "dark"
        ? "#1E1B18"
        : mode === "custom"
          ? customColors.bg
          : "#ffffff",
      color: mode === "dark"
        ? "rgba(255, 255, 255, 0.87)"
        : mode === "custom"
          ? customColors.text
          : "rgba(30, 27, 24, 0.87)",
    }}
  >
    {/* Upload Image Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-cloud-upload" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Step 1: Upload Image
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Click or drag and drop an image file (PNG, JPG, JPEG, GIF) into the upload area. This will be the base image for your occlusion question.
      </Typography>
    </Box>

    {/* Enter Statement Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-card-text" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Step 2: Enter Question Statement
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Write a clear question or instruction that tells users what they need to identify. For example: "Label the states on the map of India" or "Identify the parts of a plant cell."
      </Typography>
    </Box>

    {/* Add Tags Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-tags" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Step 3: Add Tags (Optional)
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Add comma-separated tags to organize your questions. For example: "Geography, India, Maps" or "Biology, Cell Structure, Anatomy".
      </Typography>
    </Box>

    {/* Draw Boxes Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-bounding-box" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Step 4: Draw Boxes on Labels
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
          mb: 2,
        }}
      >
        Click and drag on the image to draw colored boxes over the areas you want to hide. Each box will be automatically assigned a numbered ID.
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        After drawing each box, a dialog will appear asking you to provide the <strong>answer</strong> (what's hidden under the box) and an optional <strong>explanation</strong>.
      </Typography>
    </Box>

    {/* Review Labels Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-check-circle" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Step 5: Review Labels
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Review all your labels below the canvas. You can <strong>click a label chip</strong> to edit its answer/explanation, or <strong>delete it</strong> using the X icon. Use <strong>Clear All</strong> to remove all labels at once.
      </Typography>
    </Box>

    {/* Create Question Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <AddIcon sx={{ color: "white", fontSize: 20 }} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Step 6: Create Question
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Once you've added all labels with answers, click the <strong>Create Question</strong> button to save. Your question will appear in the Saved Questions list below.
      </Typography>
    </Box>

    {/* Managing Saved Questions Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-card-list" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Managing Saved Questions
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        In the Saved Questions section, you can:
        <br />• Click <strong>Expand/Collapse</strong> to show/hide the full question details
        <br />• Click <strong>Edit</strong> to modify the question, labels, or replace the image
        <br />• Click <strong>Delete</strong> to remove the question
        <br />• View all labels with their answers and explanations
      </Typography>
    </Box>


    {/* Pro Tips */}
    <Box
      sx={{
        p: 3,
        borderRadius: "12px",
        bgcolor: mode === "dark"
          ? "rgba(144, 202, 249, 0.05)"
          : mode === "custom"
            ? customColors.bgSecondary
            : "rgba(30, 139, 175, 0.05)",
        border: mode === "dark"
          ? "1px solid rgba(144, 202, 249, 0.2)"
          : mode === "custom"
            ? `1px solid ${customColors.border}`
            : "1px solid rgba(30, 139, 175, 0.2)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "600",
          color: mode === "dark"
            ? "#90caf9"
            : mode === "custom"
              ? customColors.text
              : "#1e8bafff",
          mb: 2,
        }}
      >
        💡 Quick Tips
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Use high-quality, clear images for better visibility of labeled areas
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Draw boxes that cover the entire label/area you want to hide
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Provide clear, concise answers for each label
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Add explanations to help learners understand why an answer is correct
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Use tags to organize questions by topic, difficulty, or category
        </Typography>
      </Box>
    </Box>
  </DialogContent>
</Dialog>

    </Box></>
    ) : (
      <>
      <Card sx={{
    mb: 3,
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    backgroundColor: mode === 'dark' ? '#1E1E2E' : '#fff',
    border: '1px solid',
    borderColor: mode === 'dark' ? '#333' : '#e0e7ff',
  }}>
    <CardContent>
      <Typography variant="h5" sx={{ mb: 2, color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#000' }}>
        Saved Questions ({savedQuestions.length})
      </Typography>

      {savedQuestions.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          p: 6,
          backgroundColor: mode === 'dark' ? '#2A2A3E' : '#f0f4ff',
          borderRadius: '16px',
          border: '2px dashed',
          borderColor: mode === 'dark' ? '#555' : '#c7d2fe',
        }}>
          <i className="bi bi-inbox" style={{
            fontSize: '3rem',
            color: mode === 'dark' ? '#667eea' : '#c7d2fe',
            marginBottom: '16px',
          }}></i>
          <Typography sx={{ fontSize: '1.2rem', color: mode === 'dark' ? '#e0e0e0' : '#666' }}>
            No questions available.
          </Typography>
        </Box>
      ) : (
        savedQuestions.map((question) => (
          <Card key={question.id} sx={{
            mb: 2,
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            backgroundColor: mode === 'dark' ? '#2A2A3E' : '#fff',
            border: '1px solid',
            borderColor: mode === 'dark' ? '#333' : '#e0e7ff',
          }}>
            <CardContent>
            
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
               <Box
  sx={{
    display: 'flex',
    gap: '8px',
    padding: '8px 12px',
  }}
>
  <IconButton
    size="small"
    onClick={() => toggleReadOnlyCardExpand(question.id)}
    title={readOnlyExpandedCards[question.id] ? "Collapse" : "Expand"}
    sx={{
      color: '#667eea',
      '&:hover': { color: '#764ba2' },
      '&:focus': { outline: 'none' },
    }}
  >
    <i className={`bi ${readOnlyExpandedCards[question.id] ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'}`}></i>
  </IconButton>

  {/* Add Eye Icon for Preview */}
<IconButton
  size="small"
  onClick={() => openPreviewDialog(question)}
  title="Preview"
  sx={{
    color: '#3b82f6',
    '&:hover': { color: '#2563eb' },
    '&:focus': { outline: 'none' },
  }}
>
  <VisibilityIcon fontSize="small" />
</IconButton>

  {/* Rest of the icons (Edit, Delete) */}
  {/* ... */}
</Box>
                <Box sx={{ flex: 1, pr: 5 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: mode === 'dark' ? '#E0E0E6' : mode === 'custom' ? customColors.text : '#000' }}>
                    <strong>Q:</strong> {question.statement}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    Labels: {question.labels?.length || 0}
                  </Typography>
                  {question.tags && question.tags.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {question.tags.map((tag, index) => (
                        <Chip key={index} label={`#${tag.name}`} size="small" />
                      ))}
                    </Box>
                  )}
                </Box>

            
              </Box>

              {/* Image and Labels (Read-Only) */}
             {readOnlyExpandedCards[question.id] && (
  <>
              {question.file_url && (
                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <canvas style={{ maxWidth: '100%', border: '1px solid #ccc', borderRadius: 8, display: 'block', margin: '0 auto' }}
                    ref={el => {
                      if (!el) return;
                      const ctx = el.getContext('2d');
                      const img = new Image();
                      img.crossOrigin = 'anonymous';
                      img.onload = () => {
                        const maxWidth = 800;
                        const aspectRatio = img.width / img.height;
                        el.width = Math.min(maxWidth, img.width);
                        el.height = el.width / aspectRatio;
                        ctx.drawImage(img, 0, 0, el.width, el.height);
                        (question.labels || []).forEach((label, index) => {
                          ctx.fillStyle = colors[index % colors.length];
                          ctx.fillRect(label.x, label.y, label.width, label.height);
                          ctx.fillStyle = '#fff';
                          ctx.font = 'bold 24px Arial';
                          ctx.textAlign = 'center';
                          ctx.textBaseline = 'middle';
                          
                        });
                      };
                     img.src = question.file_url?.startsWith("http")
  ? question.file_url
  : `${apiUrl}${question.file_url}`;
                    }} />
                </Box>
              )}

              {/* Labels (Read-Only) */}
              {question.labels && question.labels.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Labels:</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {question.labels.map((label, index) => (
                      <Card key={index} sx={{ p: 2, backgroundColor: mode === 'dark' ? '#1E1E2E' : '#f9f9f9' }}>
                        <Typography variant="body1"><strong>ID {label.id}:</strong> {label.answer}</Typography>
                        {label.explanation && (
                          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                            <strong>Explanation:</strong> {label.explanation}
                          </Typography>
                        )}
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}</>)}
            </CardContent>
          </Card>
        ))
      )}
    </CardContent>
  </Card>
   </>
    )}
  </>)
);
}  
export default ImageOcclusion;
